// assets/luxury-collection-carousel.js
// <luxury-carousel>: tabs por colección + carrusel horizontal scroll-snap + AJAX add to cart.
// Auto-init por IntersectionObserver (no en load). Sin librerías externas. Listeners passive en scroll.

(function () {
  'use strict';

  if (customElements.get('luxury-carousel')) return;

  function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class LuxuryCarousel extends HTMLElement {
    connectedCallback() {
      this._io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0].isIntersecting) return;
          obs.disconnect();
          this.init();
        },
        { rootMargin: '200px' }
      );
      this._io.observe(this);

      this._fallback = setTimeout(() => {
        if (!this._initialized) this.init();
      }, 2000);
    }

    init() {
      if (this._initialized) return;
      this._initialized = true;
      clearTimeout(this._fallback);

      this.tabs = Array.from(this.querySelectorAll('[data-lc-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-lc-panel]'));
      this.prevBtn = this.querySelector('[data-lc-prev]');
      this.nextBtn = this.querySelector('[data-lc-next]');
      this.currentEl = this.querySelector('[data-lc-current]');
      this.totalEl = this.querySelector('[data-lc-total]');

      this.activePanelIndex = 0;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const idx = parseInt(tab.dataset.index, 10);
          this.activateTab(idx);
        });
      });

      this.prevBtn?.addEventListener('click', () => this.scrollActiveList(-1));
      this.nextBtn?.addEventListener('click', () => this.scrollActiveList(1));

      this.addToCartButtons = Array.from(this.querySelectorAll('[data-lc-add-to-cart]'));
      this.addToCartButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.onAddToCart(btn));
      });

      this.bindActivePanelScroll();
      this.updateCounter();
      this.updateArrowState();
    }

    /* ===== Tabs ===== */
    activateTab(index) {
      if (index === this.activePanelIndex) return;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.activePanelIndex = index;
      this.unbindActivePanelScroll();
      this.bindActivePanelScroll();

      const activeList = this.getActiveList();
      if (activeList) activeList.scrollLeft = 0;

      this.updateCounter();
      this.updateArrowState();
    }

    getActivePanel() {
      return this.panels[this.activePanelIndex];
    }

    getActiveList() {
      const panel = this.getActivePanel();
      return panel?.querySelector('.luxury-carousel__list') || null;
    }

    /* ===== Scroll sync (contador + estado de flechas) ===== */
    bindActivePanelScroll() {
      const list = this.getActiveList();
      if (!list) return;
      this._onListScroll = debounce(() => {
        this.updateCounter();
        this.updateArrowState();
      }, 100);
      list.addEventListener('scroll', this._onListScroll, { passive: true });
    }

    unbindActivePanelScroll() {
      const list = this.getActiveList();
      if (list && this._onListScroll) {
        list.removeEventListener('scroll', this._onListScroll);
      }
    }

    getItemWidth(list) {
      const firstItem = list.querySelector('.luxury-carousel__item');
      if (!firstItem) return list.clientWidth;
      const gap = parseFloat(getComputedStyle(list).gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }

    scrollActiveList(direction) {
      const list = this.getActiveList();
      if (!list) return;
      const itemWidth = this.getItemWidth(list);
      list.scrollBy({ left: itemWidth * direction, behavior: 'smooth' });
    }

    updateCounter() {
      const list = this.getActiveList();
      if (!list || !this.currentEl || !this.totalEl) return;

      const items = list.querySelectorAll('.luxury-carousel__item');
      const total = items.length;
      const itemWidth = this.getItemWidth(list);
      const current = itemWidth > 0 ? Math.round(list.scrollLeft / itemWidth) + 1 : 1;

      this.currentEl.textContent = Math.min(current, total);
      this.totalEl.textContent = total;
    }

    updateArrowState() {
      const list = this.getActiveList();
      if (!list || !this.prevBtn || !this.nextBtn) return;

      const maxScroll = list.scrollWidth - list.clientWidth - 2; // margen de tolerancia
      this.prevBtn.disabled = list.scrollLeft <= 2;
      this.nextBtn.disabled = list.scrollLeft >= maxScroll;
    }

    /* ===== Add to cart (AJAX + cart drawer nativo si existe) ===== */
    async onAddToCart(button) {
      if (button.disabled) return;
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const iconBag = button.querySelector('[data-lc-icon-bag]');
      const iconCheck = button.querySelector('[data-lc-icon-check]');

      button.disabled = true;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || 'No se pudo agregar el producto');
        }

        // Feedback visual: bolsa -> check
        if (iconBag && iconCheck) {
          iconBag.hidden = true;
          iconCheck.hidden = false;
        }

        await this.refreshCartUI();

        setTimeout(() => {
          if (iconBag && iconCheck) {
            iconBag.hidden = false;
            iconCheck.hidden = true;
          }
          button.disabled = false;
        }, 1600);
      } catch (err) {
        button.disabled = false;
        if (iconBag && iconCheck) {
          iconBag.hidden = false;
          iconCheck.hidden = true;
        }
        button.classList.add('has-error');
        setTimeout(() => button.classList.remove('has-error'), 1500);
        console.error('luxury-carousel add to cart:', err);
      }
    }

    async refreshCartUI() {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartDrawerItems = document.querySelector('cart-drawer-items');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          const sectionsRes = await fetch(
            `${window.Shopify?.routes?.root || '/'}?sections=cart-drawer,cart-icon-bubble`
          );
          if (sectionsRes.ok) {
            const sections = await sectionsRes.json();
            cartDrawer.renderContents({ sections });
            return;
          }
        } catch (err) {
          /* si falla el render de secciones, igual abrimos el drawer abajo */
        }
      }

      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this.unbindActivePanelScroll();
    }
  }

  customElements.define('luxury-carousel', LuxuryCarousel);
})();